import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import {Alert} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import MultiSelect from "../../components/multiSelect/multiSelect";
import {searchPubs} from "../blockedPublishers/blockedPublishersService";
import downloadCsv from "../../lib/downloadCsv";
import {toast} from "react-toastify";

type LeadDownloadRow = {
    leadId: number;
    lastUpdated: string;
    leadQuality: number;
    publisherId: number;
    publisher: string;
    zip: string;
    city: string;
    state: string;
    subject: string;
    program: string;
    eduLevel: string;
    degreeSeeking: string;
    age: number | null;
    enrolled: string;
    military: string;
    whenStart: string;
    classes: string;
    working: string;
    soldCount: number;
    revenue: number;
    fraudLogixMedium: string;
    lastCcDialStatus: string;
    cakePixelFire: string;
    cakeId: string;
};

const CSV_HEADERS = [
    "lead_id",
    "last_updated",
    "lead_quality",
    "publisher_id",
    "publisher",
    "zip",
    "city",
    "state",
    "subject",
    "program",
    "edu_level",
    "degree_seeking",
    "age",
    "enrolled",
    "military",
    "when_start",
    "classes",
    "working",
    "sold_count",
    "revenue",
    "fraud_logix_medium",
    "last_cc_dial_status",
    "cake_pixel_fire",
    "cake_id",
];

function rowToCsv(r: LeadDownloadRow): any[] {
    return [
        r.leadId,
        r.lastUpdated,
        r.leadQuality,
        r.publisherId,
        r.publisher,
        r.zip,
        r.city,
        r.state,
        r.subject,
        r.program,
        r.eduLevel,
        r.degreeSeeking,
        r.age == null ? "" : r.age,
        r.enrolled,
        r.military,
        r.whenStart,
        r.classes,
        r.working,
        r.soldCount,
        r.revenue,
        r.fraudLogixMedium,
        r.lastCcDialStatus,
        r.cakePixelFire,
        r.cakeId,
    ];
}

export default function LeadDownloadPage() {
    const [dateRange, setDateRange] = React.useState(RangeToTimes("Today") as any);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(false);
    const [lastCount, setLastCount] = React.useState<number | null>(null);

    React.useEffect(() => {
        document.title = "Lead Download | DN";
    }, []);

    const runDownload = async () => {
        if (!dateRange?.[0] || !dateRange?.[1]) {
            toast.error("Select a date range");
            return;
        }
        setLoading(true);
        setLastCount(null);
        try {
            const q = gql`
                query LeadDownload($input: LeadDownloadInput!) {
                    leadDownload(input: $input) {
                        leadId
                        lastUpdated
                        leadQuality
                        publisherId
                        publisher
                        zip
                        city
                        state
                        subject
                        program
                        eduLevel
                        degreeSeeking
                        age
                        enrolled
                        military
                        whenStart
                        classes
                        working
                        soldCount
                        revenue
                        fraudLogixMedium
                        lastCcDialStatus
                        cakePixelFire
                        cakeId
                    }
                }
            `;
            const variables = {
                input: {
                    startDate: moment(dateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
                    endDate: moment(dateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
                    publisherIds: selectedPublishers.length
                        ? selectedPublishers.map((x: any) => x.id)
                        : undefined,
                },
            };
            const data = await query(q, variables);
            const rows = (data?.leadDownload as LeadDownloadRow[]) || [];
            setLastCount(rows.length);
            if (!rows.length) {
                toast.warning("No leads found");
            } else {
                await downloadCsv("lead_download.csv", [CSV_HEADERS, ...rows.map(rowToCsv)]);
                toast.success(`Downloaded ${rows.length.toLocaleString()} lead(s)`);
            }
        } catch (e: any) {
            toast.error(e?.message || "Failed to download leads");
        }
        setLoading(false);
    };

    return (
        <>
            <h2>Lead Download</h2>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1, mb: 2}}>
                <DateRange showTime={true} dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: 8}}/>
                <Box sx={{width: 220}}>
                    <MultiSelect
                        searchFunction={searchPubs}
                        selectedItems={selectedPublishers}
                        setSelectedItems={setSelectedPublishers}
                        placeholder="Publisher"
                    />
                </Box>
                <Button variant="contained" color="success" onClick={runDownload} disabled={loading}>
                    Run
                </Button>
            </Box>

            {loading ? (
                <Loading/>
            ) : lastCount == null ? (
                <Alert severity="info">Select filters and click Run to download CSV.</Alert>
            ) : lastCount === 0 ? (
                <Alert severity="warning">No leads found for that range.</Alert>
            ) : (
                <Alert severity="success">Downloaded {lastCount.toLocaleString()} lead(s).</Alert>
            )}
        </>
    );
}
